import { useEffect, useMemo, useState } from 'react';

// Accent + case-insensitive normalization. "Ardèche" → "ardeche".
export function normalizeSearch(s) {
  if (!s) return '';
  return String(s)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

// Debounced, multi-token AND search across a list of objects.
// `fields` is a list of object keys to scan. Array-typed values are joined.
// Returns { query, setQuery, debouncedQuery, filtered }.
export function useEventSearch(items, fields, debounceMs = 150) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), debounceMs);
    return () => clearTimeout(t);
  }, [query, debounceMs]);

  // Pre-compute the normalized haystack per item — re-runs only when items
  // or fields change, NOT on every keystroke.
  const haystacks = useMemo(() => {
    const map = new Map();
    for (const it of items || []) {
      const parts = [];
      for (const f of fields) {
        const v = it?.[f];
        if (Array.isArray(v)) parts.push(v.join(' '));
        else if (v != null) parts.push(v);
      }
      map.set(it.id, normalizeSearch(parts.join(' ')));
    }
    return map;
  }, [items, fields]);

  const filtered = useMemo(() => {
    const q = normalizeSearch(debouncedQuery.trim());
    if (!q) return items;
    const tokens = q.split(/\s+/).filter(Boolean);
    return (items || []).filter((it) => {
      const hay = haystacks.get(it.id) || '';
      for (const t of tokens) if (!hay.includes(t)) return false;
      return true;
    });
  }, [items, debouncedQuery, haystacks]);

  return { query, setQuery, debouncedQuery, filtered };
}
